import { LIMITS, type ListColor } from '@lista/shared-types';
import type { CreateListRequest, ListSummary } from '@lista/shared-validation';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { errorCode } from '../errors';
import { queryKeys } from '../query-keys';
import { moveListToIndex, type Neighbours, placeBetween } from '../ordering';
import type { DashboardFilters, PdfOptions } from '../services';

export function useDashboard(filters: DashboardFilters) {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.lists.dashboard(filters),
    queryFn: async () => (await services.lists.dashboard(filters)).data,
  });
}

export function useListSummary(id: string) {
  const { services } = useCore();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.lists.detail(id),
    queryFn: () => services.lists.get(id),
    // Show the dashboard card instantly while the detail loads.
    placeholderData: () => findInDashboards(queryClient, id),
  });
}

function findInDashboards(qc: QueryClient, id: string): ListSummary | undefined {
  for (const [, lists] of qc.getQueriesData<ListSummary[]>({
    queryKey: queryKeys.lists.dashboards,
  })) {
    const hit = lists?.find((l) => l.id === id);
    if (hit) return hit;
  }
  return undefined;
}

/** Writes a list everywhere it is cached (detail + every dashboard filter). */
export function patchListCaches(
  qc: QueryClient,
  id: string,
  update: (l: ListSummary) => ListSummary | null,
) {
  qc.setQueryData<ListSummary>(queryKeys.lists.detail(id), (l) => (l ? (update(l) ?? l) : l));
  qc.setQueriesData<ListSummary[]>({ queryKey: queryKeys.lists.dashboards }, (lists) =>
    lists?.flatMap((l) => {
      if (l.id !== id) return [l];
      const next = update(l);
      return next ? [next] : [];
    }),
  );
}

export function useCreateList() {
  const { services, newId } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<CreateListRequest, 'id'>) =>
      services.lists.create({ ...body, id: newId() }),
    onSuccess: (list) => {
      qc.setQueryData(queryKeys.lists.detail(list.id), list);
      void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards });
    },
  });
}

/**
 * Duplicate a list: a new list with the same colour and the same lines (quantity, unit,
 * note), all unticked. Lines are added in batches, in the original order.
 */
export function useDuplicateList() {
  const { services, newId } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ source, name }: { source: ListSummary; name: string }) => {
      const lines = await services.listItems.list(source.id);
      const copy = await services.lists.create({ name, color: source.color, id: newId() });
      try {
        for (let i = 0; i < lines.length; i += LIMITS.BATCH_ADD_MAX) {
          await services.listItems.addMany(
            copy.id,
            lines.slice(i, i + LIMITS.BATCH_ADD_MAX).map((l) => ({
              id: newId(),
              catalogItemId: l.catalogItemId,
              quantity: l.quantity,
              unit: l.unit,
              ...(l.note ? { note: l.note } : {}),
              mode: 'separate' as const,
            })),
          );
        }
      } catch (err) {
        // Never leave a half-filled copy behind.
        await services.lists.remove(copy.id).catch(() => undefined);
        throw err;
      }
      return copy;
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards }),
  });
}

export interface ReplaceListVars {
  list: ListSummary;
  name?: string;
  color?: ListColor;
  archived?: boolean;
}

/**
 * Rename / recolor / archive through PUT with If-Match. Optimistic for name and colour;
 * archive changes membership of dashboard filters, so those are refetched afterwards.
 */
export function useReplaceList() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ list, name, color, archived }: ReplaceListVars) =>
      services.lists.replace(list, {
        name: name ?? list.name,
        color: color ?? list.color,
        archived: archived ?? list.archivedAt !== null,
      }),
    onMutate: async ({ list, name, color, archived }) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists.all });
      const snapshot = qc.getQueriesData({ queryKey: queryKeys.lists.all });
      patchListCaches(qc, list.id, (l) => ({
        ...l,
        name: name ?? l.name,
        color: color ?? l.color,
        archivedAt:
          archived === undefined ? l.archivedAt : archived ? new Date().toISOString() : null,
      }));
      return { snapshot };
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    // The PUT answer carries no item previews: keep the ones the card already shows.
    onSuccess: (list) =>
      patchListCaches(qc, list.id, (old) => ({
        ...list,
        previewItems: list.previewItems ?? old.previewItems,
      })),
    onSettled: (_d, error, vars) => {
      if (vars.archived !== undefined || errorCode(error) === 'VERSION_MISMATCH') {
        void qc.invalidateQueries({ queryKey: queryKeys.lists.all });
      }
    },
  });
}

/** Soft delete (owner). Removed from every dashboard immediately; restored on failure. */
export function useDeleteList() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (list: ListSummary) => services.lists.remove(list.id),
    onMutate: async (list) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists.dashboards });
      const snapshot = qc.getQueriesData<ListSummary[]>({ queryKey: queryKeys.lists.dashboards });
      qc.setQueriesData<ListSummary[]>({ queryKey: queryKeys.lists.dashboards }, (lists) =>
        lists?.filter((l) => l.id !== list.id),
      );
      return { snapshot };
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSuccess: (_d, list) => qc.removeQueries({ queryKey: queryKeys.lists.detail(list.id) }),
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards }),
  });
}

/** Lists the user can add items to (owner/editor, not archived) - for "Add to list…". */
export function useWritableLists(enabled = true) {
  const { services } = useCore();
  const filters: DashboardFilters = { ownership: 'all', status: 'active', sort: 'updatedAt' };
  const query = useQuery({
    queryKey: queryKeys.lists.dashboard(filters),
    queryFn: async () => (await services.lists.dashboard(filters)).data,
    enabled,
  });
  return { ...query, data: query.data?.filter((l) => l.myRole !== 'VIEWER') };
}

/** Server-rendered PDF stored in object storage; resolves to a short-lived download link. */
export function useCreatePdfLink() {
  const { services } = useCore();
  return useMutation({
    mutationFn: ({ listId, options }: { listId: string; options?: PdfOptions }) =>
      services.lists.pdfLink(listId, options),
  });
}

export interface MoveListVars {
  id: string;
  neighbours: Neighbours;
}

/**
 * Drag-and-drop on the dashboard (FR-LST-06). `move(filters, id, to)` reorders the cached
 * "My order" view at once and sends the new neighbours; on failure (e.g. 409
 * STALE_POSITION after another device reordered) the cache rolls back and refetches.
 */
export function useMoveList() {
  const { services } = useCore();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, neighbours }: MoveListVars) => services.lists.move(id, neighbours),
    onMutate: async ({ id, neighbours }) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists.dashboards });
      const snapshot = qc.getQueriesData<ListSummary[]>({ queryKey: queryKeys.lists.dashboards });
      // Only views in custom order change; name/date sorts are unaffected by a move.
      for (const [key, lists] of snapshot) {
        const filters = key[2] as DashboardFilters | undefined;
        if (lists && (filters?.sort ?? 'custom') === 'custom') {
          qc.setQueryData(key, placeBetween(lists, id, neighbours));
        }
      }
      return { snapshot };
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards }),
  });

  return {
    ...mutation,
    /** Moves list `id` to index `to` of the dashboard shown with `filters`. */
    move(
      filters: DashboardFilters,
      id: string,
      to: number,
      options?: Parameters<typeof mutation.mutate>[1],
    ) {
      const visible = qc.getQueryData<ListSummary[]>(queryKeys.lists.dashboard(filters)) ?? [];
      const result = moveListToIndex(visible, id, to);
      if (result) mutation.mutate({ id, neighbours: result.neighbours }, options);
    },
  };
}

/** Pin/unpin on your own dashboard; reorders the cached views at once, rolls back on failure. */
export function usePinList() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ list, pinned }: { list: ListSummary; pinned: boolean }) =>
      services.lists.pin(list.id, pinned),
    onMutate: async ({ list, pinned }) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists.dashboards });
      const snapshot = qc.getQueriesData<ListSummary[]>({ queryKey: queryKeys.lists.dashboards });
      for (const [key, lists] of snapshot) {
        if (!lists) continue;
        const next = lists.map((l) => (l.id === list.id ? { ...l, pinned } : l));
        // Same as the server: the most recently pinned list is first, then the other pinned
        // lists, then the rest in the view's own order.
        const rest = next.filter((l) => l.id !== list.id);
        const mine = next.find((l) => l.id === list.id)!;
        qc.setQueryData(
          key,
          pinned
            ? [mine, ...rest.filter((l) => l.pinned), ...rest.filter((l) => !l.pinned)]
            : [...rest.filter((l) => l.pinned), mine, ...rest.filter((l) => !l.pinned)],
        );
      }
      return { snapshot };
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.lists.all });
    },
  });
}
